import React from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogContentText,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
  createStyles,
  makeStyles,
  useTheme,
} from '@material-ui/core';
import { Close } from '@material-ui/icons';

import colors from '../../../themes/main-colors';
import { useIsMobile } from '../../../util/style-utils';
import useContentMgmt, {
  PageTextContentType,
} from '../../../hooks/useContentMgmt';

interface HouseholdIncomeGuidelinesProps {
  isOpen: boolean;
  content: PageTextContentType;
  handleClose: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    closeBtn: {
      marginLeft: 'auto',
      padding: 0,
      [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
        padding: '12px',
      },
    },
    headingCommon: {
      color: colors.noirBlur,
      fontFamily: 'Forma-DJR-Display',
    },
    bodyText: {
      fontSize: '20px',
      lineHeight: '24px',
      marginBottom: '24px',
      color: colors.noirBlur,
      wordBreak: 'break-word',
      fontWeight: 400,
      whiteSpace: 'normal',
      fontFamily: 'Untitled-Sans',
      [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
        fontSize: '16px',
        lineHeight: '20px',
      },
    },
    listItem: {
      color: colors.noirBlur,
      fontWeight: 400,
      fontFamily: 'Untitled-Sans',
      padding: 0,
      margin: 0,
      display: 'list-item',
      listStyleType: 'disc',
      marginLeft: '40px',
      '& span': {
        fontSize: '20px',
        lineHeight: '24px',
      },
      [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
        '& span': {
          fontSize: '16px',
          lineHeight: '20px',
        },
      },
    },
    paragraph: {
      fontFamily: 'Untitled-Sans',
      fontWeight: 400,
      fontSize: '20px',
      lineHeight: 1.5,
      color: colors.noirBlur,
      [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
        fontSize: '16px',
        lineHeight: '20px',
      },
    },
    incomeGuidelineBlock: {
      display: 'flex',
      alignItems: 'center',
      border: `1px solid ${colors.lightGray1}`,
    },
    incomeGuidelineTitle: {
      padding: '12px',
      fontFamily: 'Forma-DJR-Display',
      fontWeight: 'bold',
      color: colors.white,
      backgroundColor: colors.noirBlur,
      fontSize: '1.08rem',
      minWidth: '60%',
      width: '60%',
      lineHeight: 1.167,
    },
    incomeGuidelineValue: {
      padding: '12px',
      fontFamily: 'Untitled-Sans',
      fontSize: '1.08rem',
      color: colors.noirBlur,
    },
    theadCell: {
      backgroundColor: colors.noirBlur,
      fontFamily: 'Untitled-Sans',
      fontSize: '1.125rem',
      fontWeight: 'bold',
      lineHeight: '1.167',
      letterSpacing: '0.360px',
      color: colors.white,
    },
    tCell: {
      fontSize: '1.125rem',
      fontFamily: 'Untitled-Sans',
      fontWeight: 400,
      lineHeight: '1.5',
      color: colors.noirBlur,
    },
  }),
);

export const HouseholdIncomeGuidelines: React.FC<HouseholdIncomeGuidelinesProps> = props => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useIsMobile();
  const { richText } = useContentMgmt();

  const { isOpen, content, handleClose } = props;

  const INCOME = [
    content.get('INCOME_TYPE_1'),
    content.get('INCOME_TYPE_2'),
    content.get('INCOME_TYPE_3'),
    content.get('INCOME_TYPE_4'),
    content.get('INCOME_TYPE_5'),
    content.get('INCOME_TYPE_6'),
    content.get('INCOME_TYPE_7'),
    content.get('INCOME_TYPE_8'),
    content.get('INCOME_TYPE_9'),
    content.get('INCOME_TYPE_10'),
    content.get('INCOME_TYPE_11'),
    content.get('INCOME_TYPE_12'),
    content.get('INCOME_TYPE_13'),
    content.get('INCOME_TYPE_14'),
    content.get('INCOME_TYPE_15'),
  ];

  const NOT_INCOME = [
    content.get('NOT_INCOME_TYPE_1'),
    content.get('NOT_INCOME_TYPE_2'),
    content.get('NOT_INCOME_TYPE_3'),
    content.get('NOT_INCOME_TYPE_4'),
    content.get('NOT_INCOME_TYPE_5'),
    content.get('NOT_INCOME_TYPE_6'),
    content.get('NOT_INCOME_TYPE_7'),
    content.get('NOT_INCOME_TYPE_8'),
  ];

  const AVERAGE_INCOMES = [
    content.get('ANNUAL_GROSS_INCOME_FOR_HOUSEHOLD_SIZE_1'),
    content.get('ANNUAL_GROSS_INCOME_FOR_HOUSEHOLD_SIZE_2'),
    content.get('ANNUAL_GROSS_INCOME_FOR_HOUSEHOLD_SIZE_3'),
    content.get('ANNUAL_GROSS_INCOME_FOR_HOUSEHOLD_SIZE_4'),
    content.get('ANNUAL_GROSS_INCOME_FOR_HOUSEHOLD_SIZE_5'),
    content.get('ANNUAL_GROSS_INCOME_FOR_HOUSEHOLD_SIZE_6'),
    content.get('ANNUAL_GROSS_INCOME_FOR_HOUSEHOLD_SIZE_7'),
    content.get('ANNUAL_GROSS_INCOME_FOR_HOUSEHOLD_SIZE_8'),
    content.get('ANNUAL_GROSS_INCOME_FOR_HOUSEHOLD_SIZE_9'),
    content.get('ANNUAL_GROSS_INCOME_FOR_HOUSEHOLD_SIZE_10'),
    content.get('ANNUAL_GROSS_INCOME_FOR_HOUSEHOLD_SIZE_11'),
    content.get('ANNUAL_GROSS_INCOME_FOR_HOUSEHOLD_SIZE_12'),
  ];

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullWidth={true}
      css={{
        '&>div>div': {
          minWidth: 'calc(100% - 64px)',
          [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
            minWidth: '100%',
            minHeight: '100%',
          },
        },
      }}
    >
      <IconButton
        aria-label="close"
        color="inherit"
        onClick={handleClose}
        className={classes.closeBtn}
      >
        <Close />
      </IconButton>
      <DialogContent
        css={{
          [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
            padding: '16px',
          },
        }}
      >
        <DialogContentText>
          <Typography
            className={classes.headingCommon}
            css={{
              fontSize: '20pt',
              lineHeight: '28px',
              fontWeight: 600,
              letterSpacing: '0.48px',
              marginBlockEnd: '20px',
              [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                fontSize: '20px',
                lineHeight: '26px',
              },
            }}
          >
            {content.get('HOUSEHOLD_INCOME_GUIDELINES_HEADING')}
          </Typography>
          <Typography className={classes.bodyText}>
            {content.get('HOUSEHOLD_INCOME_GUIDELINES_BODY_TEXT_1')}
          </Typography>
          <Box
            mb={'24px'}
            css={{
              width: '100%',
              padding: '16px 0px',
              borderRadius: '5px',
              backgroundColor: colors.cloudGrey,
              [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                padding: '8px 0',
              },
            }}
          >
            <Typography
              className={classes.headingCommon}
              css={{
                fontSize: '36px',
                fontWeight: 'bold',
                lineHeight: '2.5rem',
                paddingLeft: '20px',
                marginBottom: '20px',
                letterSpacing: '0.720px',
                [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                  fontSize: '24px',
                  lineHeight: '1.75rem',
                  paddingLeft: '8px',
                },
              }}
            >
              {content.get('INCOME_TYPES_HEADING')}
            </Typography>
            <Box
              display={'flex'}
              css={{
                [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                  flexDirection: 'column',
                },
              }}
            >
              <Box
                borderRight={`1px solid ${colors.lightGray1}`}
                flexGrow={1}
                padding={'0 20px'}
                css={{
                  [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                    padding: '0 8px',
                    marginBottom: '48px',
                  },
                }}
              >
                <Typography
                  className={classes.headingCommon}
                  css={{
                    fontSize: '20pt',
                    fontWeight: 'bold',
                    lineHeight: '28px',
                    margin: '0 0 14px 0',
                    letterSpacing: '0.48px',
                    [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                      fontSize: '20px',
                      lineHeight: '26px',
                    },
                  }}
                >
                  {content.get('INCOME')}
                </Typography>
                <List css={{ padding: 0 }}>
                  {INCOME.map((type, idx) => {
                    return (
                      <ListItem
                        key={idx}
                        component={'li'}
                        className={classes.listItem}
                      >
                        <ListItemText primary={type} />
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
              <Box
                flexGrow={1}
                padding={'0 20px'}
                css={{
                  [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                    padding: '0 8px',
                  },
                }}
              >
                <Typography
                  className={classes.headingCommon}
                  css={{
                    fontSize: '20pt',
                    fontWeight: 'bold',
                    lineHeight: '28px',
                    margin: '0 0 14px 0',
                    letterSpacing: '0.48px',
                    [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                      fontSize: '20px',
                      lineHeight: '26px',
                    },
                  }}
                >
                  {content.get('NOT_INCOME')}
                </Typography>
                <List css={{ padding: 0 }}>
                  {NOT_INCOME.map((type, idx) => {
                    return (
                      <ListItem
                        key={idx}
                        component={'li'}
                        className={classes.listItem}
                      >
                        <ListItemText primary={type} />
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            </Box>
          </Box>

          <Box>
            <Typography
              css={{
                color: colors.noirBlur,
                fontSize: '36px',
                fontWeight: 300,
                lineHeight: '2.5rem',
                [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                  fontSize: '24px',
                  lineHeight: '1.75rem',
                  margin: '48px 0 24px 0',
                },
              }}
            >
              {content.get('HOUSEHOLD_INCOME_GUIDELINES')}
            </Typography>
            <Typography className={classes.paragraph}>
              {richText(content.get('HOUSEHOLD_INCOME_GUIDELINES_BODY_TEXT_2'))}
            </Typography>

            {isMobile ? (
              <Box display={'flex'} flexDirection={'column'}>
                {AVERAGE_INCOMES.map((income, idx) => {
                  return (
                    <Box css={{ margin: '8px 0' }} key={idx}>
                      <Box className={classes.incomeGuidelineBlock}>
                        <Typography
                          className={classes.incomeGuidelineTitle}
                          component={'h4'}
                        >
                          {content.get('HOUSEHOLD_SIZE')}
                        </Typography>
                        <Typography
                          className={classes.incomeGuidelineValue}
                          component={'p'}
                        >
                          {idx + 1}
                        </Typography>
                      </Box>
                      <Box className={classes.incomeGuidelineBlock}>
                        <Typography
                          className={classes.incomeGuidelineTitle}
                          component={'h4'}
                        >
                          {content.get('AVG_GROSS_ANNUAL_HOUSEHOLD_INCOME')}
                        </Typography>
                        <Typography
                          className={classes.incomeGuidelineValue}
                          component={'p'}
                        >
                          {income}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
                <Box css={{ margin: '8px 0' }}>
                  <Box className={classes.incomeGuidelineBlock}>
                    <Typography
                      className={classes.incomeGuidelineTitle}
                      component={'h4'}
                    >
                      {content.get('HOUSEHOLD_SIZE')}
                    </Typography>
                    <Typography
                      className={classes.incomeGuidelineValue}
                      component={'p'}
                    >
                      {content.get('EACH_ADDITIONAL_MEMBER')}
                    </Typography>
                  </Box>
                  <Box className={classes.incomeGuidelineBlock}>
                    <Typography
                      className={classes.incomeGuidelineTitle}
                      component={'h4'}
                    >
                      {content.get('AVG_GROSS_ANNUAL_HOUSEHOLD_INCOME')}
                    </Typography>
                    <Typography
                      className={classes.incomeGuidelineValue}
                      component={'p'}
                    >
                      {content.get('ADDITIONAL_MEMBER_INCOME')}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                css={{
                  boxShadow:
                    '0 1px 3px -2px rgba(0, 0, 40, .12), 0 3px 6px 0 rgba(0, 0, 40, .16)',
                  [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                    marginTop: '16px',
                  },
                }}
              >
                <Table
                  css={{ borderCollapse: 'collapse', borderColor: 'gray' }}
                >
                  <TableHead css={{ borderRadius: '5px 0px 0px 0px' }}>
                    <TableRow>
                      <TableCell className={classes.theadCell}>
                        {content.get('HOUSEHOLD_SIZE')}
                      </TableCell>
                      <TableCell className={classes.theadCell}>
                        {content.get('AVG_GROSS_ANNUAL_HOUSEHOLD_INCOME')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {AVERAGE_INCOMES.map((income, idx) => {
                      return (
                        <TableRow>
                          <TableCell className={classes.tCell}>
                            {idx + 1}
                          </TableCell>
                          <TableCell className={classes.tCell}>
                            {income}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow>
                      <TableCell className={classes.tCell}>
                        {content.get('EACH_ADDITIONAL_MEMBER')}
                      </TableCell>
                      <TableCell className={classes.tCell}>
                        {content.get('ADDITIONAL_MEMBER_INCOME')}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </DialogContentText>
      </DialogContent>
    </Dialog>
  );
};
